/**
 * Управление туром со страниц (E4.6b): начать урок, приостановить, продолжить, закрыть.
 * Начало запоминает точку возврата -- адрес, прокрутку, состояние карты и фокус. Урок не
 * начинается поверх незавершённых изменений страницы: форма остаётся на месте, как была.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { hasDirtyDrafts } from '@/stores/workspaceDrafts';
import type { LessonId } from './catalog';
import { captureMapState } from './mapReturnState';
import { TRAINING_TARGET_ATTRIBUTE } from './targets';
import { beginTour, leaveTour, resumeTour, useTourStore, type TourReturnState } from './tourStore';
import { useTrainingLauncher } from './useTrainingLauncher';
import { useTrainingProgress } from './useTrainingProgress';
import { pauseExercise, useExerciseStore } from './exerciseStore';

/** Как найти элемент фокуса после возврата: цель тура или id */
function focusTargetOf(element: Element | null): string | null {
	if (!(element instanceof HTMLElement) || element === document.body) return null;
	const target = element.closest<HTMLElement>(`[${TRAINING_TARGET_ATTRIBUTE}]`);
	if (target?.dataset.trainingTarget) return target.dataset.trainingTarget.split(' ')[0];
	return element.id || null;
}

export function useTrainingTour() {
	const { t } = useTranslation('learning');
	const tour = useTourStore((state) => state.tour);
	const location = useLocation();
	const { progress } = useTrainingProgress();
	const { manifestOf, newExample } = useTrainingLauncher();

	const origin = useCallback(
		(): TourReturnState => ({
			href: `${location.pathname}${location.search}`,
			scroll: { x: window.scrollX, y: window.scrollY },
			mapState: captureMapState(),
			focusTarget: focusTargetOf(document.activeElement),
		}),
		[location.pathname, location.search],
	);

	/** Изменения страницы сначала сохраняются или отменяются -- тур их не трогает */
	const canStart = useCallback(() => {
		if (!hasDirtyDrafts()) return true;
		toast.error(t('tour.dirtyStart'));
		return false;
	}, [t]);

	const startLesson = useCallback(
		async (lesson: LessonId, options: { restart?: boolean; welcome?: boolean } = {}) => {
			if (!canStart()) return;
			const saved = progress.lessons[lesson];
			const index = options.restart || saved?.status === 'completed' ? 0 : (saved?.cursor ?? 0);
			try {
				const manifest = await manifestOf();
				if (manifest === null) {
					/* Повтор не поможет: пример удалён или истёк -- предлагается новый */
					toast.error(t('page.instanceGone'), {
						action: { label: t('page.newExample'), onClick: () => void newExample() },
					});
					return;
				}
				const activeExercise = useExerciseStore.getState().active;
				if (activeExercise !== null)
					pauseExercise(progress.exercises[activeExercise]?.status === 'completed');
				beginTour({ type: 'start', lesson, index, welcome: options.welcome }, manifest, origin());
			} catch {
				toast.error(t('page.openFailed'));
			}
		},
		[canStart, manifestOf, newExample, origin, progress.lessons, progress.exercises, t],
	);

	const pauseLesson = useCallback(() => leaveTour('pause', true), []);
	const closeLesson = useCallback(() => leaveTour('close', true), []);
	const resumeLesson = useCallback(() => {
		if (!canStart()) return;
		if (tour.phase === 'paused') resumeTour(origin());
		else if (progress.last) void startLesson(progress.last);
	}, [canStart, origin, progress.last, startLesson, tour.phase]);

	return { tour, startLesson, pauseLesson, resumeLesson, closeLesson };
}
